import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getExpensesByCategoryReport, getExpensesBySupplierReport, getStores } from '../api/api';
import { BarChart3, TrendingUp, Filter, Download, FileSpreadsheet, FileText } from 'lucide-react';
import { exportAnnualReportToExcel, exportAnnualReportToPDF } from '../utils/exportUtils';

const AnnualReports = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('categories'); // 'categories' | 'suppliers'
  const [categoryReport, setCategoryReport] = useState(null);
  const [supplierReport, setSupplierReport] = useState(null);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    store_id: ''
  });

  const isAdmin = ['admin', 'manager', 'supervisor', 'admin_assistant'].includes(user?.role);
  const canViewSupplierReport = ['admin', 'manager', 'supervisor', 'store_manager', 'purchase_manager'].includes(user?.role);
  const isPurchaseManager = user?.role === 'purchase_manager';

  useEffect(() => {
    loadStores();
    // Si es purchase_manager, forzar el tab de suppliers
    if (isPurchaseManager && activeTab !== 'suppliers') {
      setActiveTab('suppliers');
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [filters, activeTab]);

  const loadStores = async () => {
    if (isAdmin) {
      try {
        const data = await getStores();
        setStores(data);
      } catch (error) {
        console.error('Error loading stores:', error);
      }
    }
  };

  const handleExportExcel = () => {
    const report = activeTab === 'categories' ? categoryReport : supplierReport;
    if (!report) return;
    
    const filename = `reporte-${activeTab === 'categories' ? 'gastos-categoria' : 'compras-proveedor'}-${filters.year}`;
    const title = activeTab === 'categories' ? 'Reporte de Gastos por Categoría' : 'Reporte de Compras por Proveedor';
    
    exportAnnualReportToExcel(report, filename, activeTab);
  };

  const handleExportPDF = () => {
    const report = activeTab === 'categories' ? categoryReport : supplierReport;
    if (!report) return;
    
    const filename = `reporte-${activeTab === 'categories' ? 'gastos-categoria' : 'compras-proveedor'}-${filters.year}`;
    const title = activeTab === 'categories' 
      ? `Reporte Anual de Gastos por Categoría - ${filters.year}` 
      : `Reporte Anual de Compras por Proveedor - ${filters.year}`;
    
    exportAnnualReportToPDF(report, title, filename, activeTab);
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      const params = { year: filters.year };
      if (filters.store_id) {
        params.store_id = filters.store_id;
      }

      if (activeTab === 'categories') {
        const data = await getExpensesByCategoryReport(params);
        setCategoryReport(data);
      } else if (activeTab === 'suppliers' && canViewSupplierReport) {
        const data = await getExpensesBySupplierReport(params);
        setSupplierReport(data);
      }
    } catch (error) {
      console.error('Error loading report:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `Q${amount.toFixed(2)}`;
  };

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 5; y--) {
    years.push(y);
  }

  const renderCategoryReport = () => {
    if (!categoryReport) return null;

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50">
                Categoría
              </th>
              {categoryReport.months.map((month) => (
                <th key={`cat-month-${month}`} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {month}
                </th>
              ))}
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                Total
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50">
                %
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {categoryReport.categories.map((cat) => (
              <tr key={cat.category} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900 sticky left-0 bg-white">
                  {cat.category}
                </td>
                {cat.months.map((amount, mIdx) => (
                  <td key={`${cat.category}-m${mIdx}`} className="px-3 py-3 text-center text-gray-600">
                    {amount > 0 ? formatCurrency(amount) : '-'}
                  </td>
                ))}
                <td className="px-4 py-3 text-center font-semibold text-blue-600 bg-blue-50">
                  {formatCurrency(cat.total)}
                </td>
                <td className="px-4 py-3 text-center font-semibold text-green-600 bg-green-50">
                  {cat.percentage}%
                </td>
              </tr>
            ))}
            {/* Fila de totales */}
            <tr className="bg-gray-100 font-bold">
              <td className="px-4 py-3 whitespace-nowrap text-gray-900 sticky left-0 bg-gray-100">
                TOTAL
              </td>
              {categoryReport.monthly_totals.map((total, idx) => (
                <td key={`cat-total-${idx}`} className="px-3 py-3 text-center text-gray-900">
                  {total > 0 ? formatCurrency(total) : '-'}
                </td>
              ))}
              <td className="px-4 py-3 text-center text-blue-800 bg-blue-100">
                {formatCurrency(categoryReport.grand_total)}
              </td>
              <td className="px-4 py-3 text-center text-green-800 bg-green-100">
                100%
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  const renderSupplierReport = () => {
    if (!supplierReport) return null;

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50">
                Proveedor
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider notranslate" translate="no">
                NIT
              </th>
              {supplierReport.months.map((month) => (
                <th key={`sup-month-${month}`} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {month}
                </th>
              ))}
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                Total
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50">
                %
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {supplierReport.suppliers.map((sup) => (
              <tr key={sup.supplier_name} className="hover:bg-gray-50">
                <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900 sticky left-0 bg-white">
                  {sup.supplier_name}
                </td>
                <td className="px-3 py-3 text-gray-600">
                  {sup.supplier_nit}
                </td>
                {sup.months.map((amount, mIdx) => (
                  <td key={`${sup.supplier_name}-m${mIdx}`} className="px-3 py-3 text-center text-gray-600">
                    {amount > 0 ? formatCurrency(amount) : '-'}
                  </td>
                ))}
                <td className="px-4 py-3 text-center font-semibold text-blue-600 bg-blue-50">
                  {formatCurrency(sup.total)}
                </td>
                <td className="px-4 py-3 text-center font-semibold text-green-600 bg-green-50">
                  {sup.percentage}%
                </td>
              </tr>
            ))}
            {/* Fila de totales */}
            <tr className="bg-gray-100 font-bold">
              <td className="px-4 py-3 whitespace-nowrap text-gray-900 sticky left-0 bg-gray-100">
                TOTAL
              </td>
              <td className="px-3 py-3"></td>
              {supplierReport.monthly_totals.map((total, idx) => (
                <td key={`sup-total-${idx}`} className="px-3 py-3 text-center text-gray-900">
                  {total > 0 ? formatCurrency(total) : '-'}
                </td>
              ))}
              <td className="px-4 py-3 text-center text-blue-800 bg-blue-100">
                {formatCurrency(supplierReport.grand_total)}
              </td>
              <td className="px-4 py-3 text-center text-green-800 bg-green-100">
                100%
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reportes Anuales</h1>
          <p className="text-gray-600 mt-1">Análisis de gastos y compras por período</p>
        </div>
        
        {/* Botones de Exportación */}
        <div className="flex space-x-2">
          <button
            onClick={handleExportExcel}
            disabled={!categoryReport && !supplierReport}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <FileSpreadsheet size={18} />
            <span>Exportar Excel</span>
          </button>
          <button
            onClick={handleExportPDF}
            disabled={!categoryReport && !supplierReport}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <FileText size={18} />
            <span>Exportar PDF</span>
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex items-center space-x-2 mb-4">
          <Filter size={20} className="text-gray-500" />
          <h2 className="text-lg font-semibold">Filtros</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Año</label>
            <select
              value={filters.year}
              onChange={(e) => setFilters({ ...filters, year: parseInt(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {isAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tienda</label>
              <select
                value={filters.store_id}
                onChange={(e) => setFilters({ ...filters, store_id: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas las tiendas</option>
                {stores.map((store) => (
                  <option key={store.id} value={store.id}>{store.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-end">
            <button
              onClick={loadReports}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <BarChart3 size={20} />
              <span>Generar Reporte</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="border-b">
          <nav className="flex">
            {!isPurchaseManager && (
              <button
                onClick={() => setActiveTab('categories')}
                className={`px-6 py-3 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === 'categories'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <BarChart3 size={18} />
                <span>Gastos por Categoría</span>
              </button>
            )}
            {canViewSupplierReport && (
              <button
                onClick={() => setActiveTab('suppliers')}
                className={`px-6 py-3 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === 'suppliers'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <TrendingUp size={18} />
                <span>Compras por Proveedor</span>
              </button>
            )}
          </nav>
        </div>

        {/* Contenido */}
        <div className="p-4">
          {loading ? (
            <div className="text-center py-12 text-gray-600">
              Cargando reporte...
            </div>
          ) : (
            <>
              {activeTab === 'categories' && !isPurchaseManager && (
                <>
                  {categoryReport && categoryReport.categories.length > 0 ? (
                    <>
                      <div className="mb-4 flex justify-between items-center">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            Reporte de Gastos por Categoría - {filters.year}
                          </h3>
                          <p className="text-sm text-gray-500">
                            Total anual: {formatCurrency(categoryReport.grand_total)}
                          </p>
                        </div>
                      </div>
                      {renderCategoryReport()}
                    </>
                  ) : (
                    <div className="text-center py-12 text-gray-600">
                      No hay datos de gastos para el período seleccionado
                    </div>
                  )}
                </>
              )}

              {activeTab === 'suppliers' && canViewSupplierReport && (
                <>
                  {supplierReport && supplierReport.suppliers.length > 0 ? (
                    <>
                      <div className="mb-4 flex justify-between items-center">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            Reporte de Compras por Proveedor - {filters.year}
                          </h3>
                          <p className="text-sm text-gray-500">
                            Total anual: {formatCurrency(supplierReport.grand_total)}
                          </p>
                        </div>
                      </div>
                      {renderSupplierReport()}
                    </>
                  ) : (
                    <div className="text-center py-12 text-gray-600">
                      No hay datos de compras a proveedores para el período seleccionado
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnnualReports;
